import React, { useState, useEffect } from 'react';
import apiFetch from '../../../../../utils/api';
import { Upload, Trash2, FilePlus, AlertCircle, CheckCircle2, ImageIcon } from 'lucide-react';
import './TutorialAdmin.css';

const TutorialAdmin = () => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState(null);

  // Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fileType, setFileType] = useState('PDF');
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [isActive, setIsActive] = useState(true);
  
  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const res = await apiFetch('/api/admin/tutorials');
      const d = await res.json();
      setTutorials(d.tutorials || []);
    } catch (err) {
      console.error('Error fetching tutorials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !title) {
      setStatus({ type: 'error', message: 'Title and File are required.' });
      return;
    }

    setIsUploading(true);
    setStatus({ type: 'info', message: 'Uploading file and syncing with database...' });

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('fileType', fileType);
    formData.append('file', file);
    if (thumbnail) formData.append('thumbnail', thumbnail);
    formData.append('isActive', isActive);

    try {
      const res = await apiFetch('/api/admin/tutorials/create', {
        method: 'POST',
        body: formData,
      });

      const d = await res.json();
      if (d.success) {
        setStatus({ type: 'success', message: 'Tutorial uploaded successfully!' });
        resetForm();
        fetchTutorials();
      } else {
        setStatus({ type: 'error', message: d.message || 'Upload failed.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Network error during upload.' });
    } finally {
      setIsUploading(false);
    }
  };

  const confirmDelete = (id) => {
    setDeletingId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    
    try {
      const res = await apiFetch(`/api/admin/tutorials/${deletingId}/delete`, { method: 'DELETE' });
      const d = await res.json();
      if (d.success) {
        fetchTutorials();
      }
    } catch (err) {
       alert('Failed to delete tutorial.');
    } finally {
      setShowDeleteModal(false);
      setDeletingId(null);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setFileType('PDF');
    setFile(null);
    setThumbnail(null);
    setIsActive(true);
    // Reset file inputs manually
    const fileInput = document.getElementById('tut-file');
    if (fileInput) fileInput.value = '';
    const thumbInput = document.getElementById('tut-thumb');
    if (thumbInput) thumbInput.value = '';
  };

  return (
    <div className="tut-admin">
      <div className="tut-admin__header">
        <h1 className="tut-admin__title">Manage Tutorials & Docs</h1>
        <p className="tut-admin__subtitle">Upload guides, training videos, and documentation for your users.</p>
      </div>

      <div className="tut-admin__grid">
        {/* Upload Form */}
        <div className="tut-admin__section">
          <div className="tut-admin__card">
            <div className="tut-admin__card-header">
              <FilePlus size={20} className="text-indigo-500" />
              <h2>Upload New Material</h2>
            </div>

            <form className="tut-form" onSubmit={handleUpload}>
              <div className="tut-form__group">
                <label>Tutorial Title</label>
                <input 
                  type="text" 
                  placeholder="e.g. How to Withdraw Earnings" 
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isUploading}
                />
              </div>

              <div className="tut-form__group">
                <label>Short Description (Optional)</label>
                <textarea 
                  placeholder="Provide context for this material..." 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isUploading}
                />
              </div>

              <div className="tut-form__row">
                <div className="tut-form__group">
                  <label>Material Type</label>
                  <select value={fileType} onChange={(e) => setFileType(e.target.value)} disabled={isUploading}>
                    <option value="PDF">PDF Document</option>
                    <option value="Image">Photo / Image</option>
                    <option value="Video">Video Tutorial</option>
                  </select>
                </div>
                <div className="tut-form__group">
                  <label>Visibility</label>
                  <select value={isActive} onChange={(e) => setIsActive(e.target.value === 'true')} disabled={isUploading}>
                    <option value="true">Published</option>
                    <option value="false">Hidden / Draft</option>
                  </select>
                </div>
              </div>

              <div className="tut-form__group">
                <label>Source File ({fileType})</label>
                <div className="tut-form__file-input">
                  <Upload size={18} />
                  <input 
                    id="tut-file"
                    type="file" 
                    onChange={(e) => setFile(e.target.files[0])}
                    accept={fileType === 'PDF' ? '.pdf' : fileType === 'Image' ? 'image/*' : 'video/*'}
                    disabled={isUploading}
                  />
                </div>
                <span className="tut-form__hint">Max size: 50MB</span>
              </div>

              {fileType === 'Video' && (
                <div className="tut-form__group">
                  <label>Thumbnail Image (Optional)</label>
                  <div className="tut-form__file-input">
                    <ImageIcon size={18} />
                    <input 
                      id="tut-thumb"
                      type="file" 
                      onChange={(e) => setThumbnail(e.target.files[0])}
                      accept="image/*"
                      disabled={isUploading}
                    />
                  </div>
                </div>
              )}

              {status && (
                <div className={`tut-form__status tut-form__status--${status.type}`}>
                  {status.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
                  <span>{status.message}</span>
                </div>
              )}

              <button className="tut-form__submit" disabled={isUploading}>
                {isUploading ? 'Preparing Material...' : 'Publish Tutorial'}
              </button>
            </form>
          </div>
        </div>

        {/* List View */}
        <div className="tut-admin__section">
          <div className="tut-admin__card">
            <div className="tut-admin__card-header">
              <h2>Existing Content</h2>
              <span className="tut-admin__counter">{tutorials.length} total</span>
            </div>

            <div className="tut-list">
              {loading ? (
                <div className="tut-list__empty">Loading...</div>
              ) : tutorials.length === 0 ? (
                <div className="tut-list__empty">No tutorials uploaded yet.</div>
              ) : (
                tutorials.map(t => (
                  <div key={t.id} className="tut-item">
                     <div className="tut-item__info">
                        <div className={`tut-item__icon type-${t.fileType.toLowerCase()}`}>
                          {t.fileType[0]}
                        </div>
                        <div>
                          <h4 className="tut-item__title">{t.title}</h4>
                          <span className="tut-item__meta">{t.fileType} • {t.createdAt}</span>
                        </div>
                     </div>
                     <div className="tut-item__actions">
                        <span className={`tut-item__status ${t.isActive ? 'active' : 'inactive'}`}>
                          {t.isActive ? 'Published' : 'Hidden'}
                        </span>
                        <button className="tut-item__delete" onClick={() => confirmDelete(t.id)}>
                          <Trash2 size={16} />
                        </button>
                     </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showDeleteModal && (
        <div className="tut-modal-overlay">
          <div className="tut-modal">
            <div className="tut-modal__icon">
              <Trash2 size={32} />
            </div>
            <h3>Remove Tutorial?</h3>
            <p>Are you sure you want to delete this material? This action is permanent and cannot be undone.</p>
            <div className="tut-modal__actions">
              <button 
                className="tut-modal__btn tut-modal__btn--cancel" 
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="tut-modal__btn tut-modal__btn--delete" 
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialAdmin;
;
